import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../services/api';
import { demoPosPath } from '../demoPosPaths';
import { rememberBillBranch, useBranches } from '../context/BranchContext';
import useAsync from '../hooks/useAsync';
import useBillCart from '../hooks/useBillCart';
import useCatalog from '../hooks/useCatalog';
import BranchPickerModal from '../components/branch/BranchPickerModal';
import CartPanel from '../components/wizard/CartPanel';
import StepRail from '../components/wizard/StepRail';
import IdentifyStep from '../components/wizard/steps/IdentifyStep';
import ServicesStep from '../components/wizard/steps/ServicesStep';
import BillStep from '../components/wizard/steps/BillStep';
import PaymentStep from '../components/wizard/steps/PaymentStep';
import ReviewStep from '../components/wizard/steps/ReviewStep';
import ApproveStep from '../components/wizard/steps/ApproveStep';
import DeliverStep from '../components/wizard/steps/DeliverStep';
import printReceipt from '../components/receipt/printReceipt';
import { BillStatus } from '../components/common/Badges';
import { EmptyState, Loader } from '../components/common/Feedback';
import { BackIcon, PrintIcon, WhatsAppIcon } from '../components/common/icons';
import { canEditServices, stepForBill } from '../utils/billSteps';
import { formatMoney, pendingAmount } from '../utils/money';
import { lineFromItem } from '../utils/services';

const STEP = { identify: 0, services: 1, bill: 2, pay: 3, review: 4, approve: 5, deliver: 6 };

function savedTotals(bill) {
  const gross = (bill.items || []).reduce((s, i) => s + (Number(i.unit_price) || 0) * (Number(i.quantity) || 1), 0);
  const subtotal = Number(bill.subtotal) || 0;
  return {
    gross,
    lineOffers: Math.max(gross - subtotal, 0),
    billOffers: Number(bill.discount_amount) || 0,
    tax: Number(bill.tax_amount) || 0,
    grand: Number(bill.grand_total) || 0,
    taxRate: Number(bill.tax_rate) || 0,
  };
}

/** One bill, start to finish: customer → services → bill → payment → review → approve → print & WhatsApp. */
export default function BillWizard() {
  const { billId } = useParams();
  const { branchName } = useBranches();
  const billQ = useAsync(() => api.bill(billId), [billId]);
  const bill = billQ.data;
  const setBill = billQ.setData;

  const [step, setStep] = useState(null);
  const [busy, setBusy] = useState(false);
  const [hint, setHint] = useState('');
  const [pay, setPay] = useState({ method: null, amount: '', upiRef: '' });
  const [managerName, setManagerName] = useState('');
  const [branchPicker, setBranchPicker] = useState(false);
  const [repriceFor, setRepriceFor] = useState(null);

  const cart = useBillCart(bill);
  const catalog = useCatalog(bill?.franchise_id);

  useEffect(() => {
    if (bill && step === null) setStep(stepForBill(bill));
    if (bill?.manager_name && !managerName) setManagerName(bill.manager_name);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill]);

  // After a branch change, move the order to the new branch's prices once that list has loaded.
  useEffect(() => {
    if (repriceFor && String(bill?.franchise_id) === repriceFor && !catalog.loading && catalog.services.length) {
      cart.reprice(catalog.services);
      setRepriceFor(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repriceFor, catalog.loading, catalog.services, bill?.franchise_id]);

  const stylistNames = useMemo(() => {
    const names = catalog.stylists.map((s) => s.name);
    cart.lines.forEach((l) => l.stylist_name && !names.includes(l.stylist_name) && names.push(l.stylist_name));
    return names;
  }, [catalog.stylists, cart.lines]);

  if (billQ.loading && !bill) return <Loader text="Opening bill…" />;
  if (!bill) {
    return (
      <EmptyState
        title="Bill not found"
        body={billQ.error}
        action={
          <Link className="dp-btn" to={demoPosPath()}>
            Back to queue
          </Link>
        }
      />
    );
  }

  const current = step ?? 0;
  const reached = Math.max(stepForBill(bill), current);
  const editable = canEditServices(bill);
  const canOpen = (i) => {
    if (i === STEP.identify || i === STEP.services) return editable && (i === STEP.identify || Boolean(bill.customer));
    return i <= stepForBill(bill);
  };
  const go = (i) => {
    setHint('');
    setStep(i);
  };

  const act = async (fn, failMsg) => {
    setBusy(true);
    setHint('');
    try {
      await fn();
    } catch (e) {
      setHint(e.message || failMsg);
      toast.error(e.message || failMsg);
    } finally {
      setBusy(false);
    }
  };

  const saveServices = () =>
    act(async () => {
      const updated = await api.setItems(bill.id, cart.payload());
      setBill(updated);
      go(STEP.bill);
    }, 'Could not save the services');

  const collect = () =>
    act(async () => {
      const amount = Number(pay.amount) || 0;
      const updated =
        pay.method === 'cash'
          ? await api.pay(bill.id, { method: 'cash', amount })
          : await api.pay(bill.id, {
              method: 'manual_upi',
              amount,
              verified: true,
              upi_txn_id: pay.upiRef.trim() || `UPI-${Date.now()}`,
            });
      setBill(updated);
      setPay({ method: null, amount: '', upiRef: '' });
      toast.success(`${pay.method === 'cash' ? 'Cash' : 'UPI'} payment of ${formatMoney(amount)} recorded`);
      go(STEP.review);
    }, 'Payment failed');

  const confirmManager = () =>
    act(async () => {
      const name = managerName.trim();
      const updated = await api.confirmManager(bill.id, { manager_name: name, manager_id: name });
      setBill(updated);
      go(STEP.approve);
    }, 'Could not confirm the payment');

  const approve = () =>
    act(async () => {
      if (bill.status === 'under_review') await api.approve(bill.id, { approved_by: bill.manager_name || 'Manager' });
      const updated = await api.finalize(bill.id);
      setBill(updated);
      toast.success(`Invoice ${updated.invoice?.invoice_number || ''} created`);
      go(STEP.deliver);
    }, 'Could not approve the bill');

  const deliver = (what) =>
    act(async () => {
      if (what === 'print') printReceipt();
      const updated = await api.deliver(bill.id, { print: what === 'print', whatsapp: what === 'whatsapp' });
      setBill(updated);
      const result = updated.delivery?.[what];
      if (what === 'whatsapp') {
        if (result?.error) throw new Error(result.error);
        if (result?.mode === 'noop') toast.success('WhatsApp marked as sent (WhatsApp is not set up on the POS server)');
        else if (result?.warning) toast(result.warning, { duration: 8000 });
        else toast.success(`Invoice PDF sent on WhatsApp to +${result?.phone || ''}`);
      }
    }, 'Delivery failed');

  const changeBranch = (franchiseId) =>
    act(async () => {
      rememberBillBranch(franchiseId);
      const updated = await api.setBranch(bill.id, { franchise_id: franchiseId, franchise_name: branchName(franchiseId) });
      setBill(updated);
      setRepriceFor(String(franchiseId));
      setBranchPicker(false);
      toast.success(`Branch changed to ${updated.franchise_name || branchName(franchiseId)}`);
    }, 'Could not change the branch');

  const due = pendingAmount(bill);
  let cta = null;
  let extraActions = null;
  switch (current) {
    case STEP.identify:
      cta = { label: bill.customer ? 'Continue to services' : 'Find the customer first', disabled: !bill.customer, onClick: () => go(STEP.services) };
      break;
    case STEP.services:
      cta = {
        label: 'Save bill',
        busy,
        disabled: !cart.lines.length || cart.missingPrice || !bill.franchise_id,
        onClick: saveServices,
      };
      break;
    case STEP.bill:
      cta = { label: 'Take payment', disabled: !(bill.items || []).length, onClick: () => go(STEP.pay) };
      break;
    case STEP.pay: {
      const amount = Number(pay.amount) || 0;
      const full = amount > 0 && amount >= due - 0.009;
      cta = {
        label: !pay.method ? 'Choose cash or UPI' : full ? `Collect ${formatMoney(amount)}` : `Enter ${formatMoney(due)}`,
        busy,
        disabled: !pay.method || !full,
        onClick: collect,
      };
      break;
    }
    case STEP.review:
      cta = { label: 'Confirm payment', busy, disabled: !managerName.trim(), onClick: confirmManager };
      break;
    case STEP.approve:
      cta = { label: 'Approve & create invoice', busy, onClick: approve };
      break;
    default:
      cta = null;
      extraActions = (
        <div className="dp-cart-actions">
          <button type="button" className="dp-btn dp-btn--block" onClick={() => deliver('print')} disabled={busy}>
            <PrintIcon size={16} /> Print receipt
          </button>
          <button type="button" className="dp-btn dp-btn--block dp-btn--ghost" onClick={() => deliver('whatsapp')} disabled={busy || !bill.customer?.phone}>
            <WhatsAppIcon size={16} /> Send on WhatsApp
          </button>
          <Link className="dp-btn dp-btn--block dp-btn--ghost" to={demoPosPath()}>
            Next customer
          </Link>
        </div>
      );
  }

  const onServices = current === STEP.services;
  const cartLines = onServices ? cart.lines : (bill.items || []).map(lineFromItem);

  return (
    <div className="dp-wizard">
      <div className="dp-wizard-head">
        <Link to={demoPosPath()} className="dp-back">
          <BackIcon size={16} /> Queue
        </Link>
        <div className="dp-wizard-title">
          <h1>{bill.customer?.name || 'New bill'}</h1>
          <span>
            {bill.franchise_name || 'No branch yet'}
            {bill.invoice?.invoice_number ? ` · ${bill.invoice.invoice_number}` : ''}
          </span>
        </div>
        <BillStatus status={bill.status} />
      </div>

      <StepRail current={current} reached={reached} canOpen={canOpen} onOpen={go} />

      <div className="dp-wizard-body">
        <section className="dp-panel">
          {current === STEP.identify ? (
            <IdentifyStep
              bill={bill}
              onIdentified={(updated) => {
                setBill(updated);
                go(STEP.services);
              }}
              onContinue={() => go(STEP.services)}
            />
          ) : null}
          {current === STEP.services ? (
            <ServicesStep
              bill={bill}
              catalog={catalog}
              lines={cart.lines}
              onToggle={cart.toggle}
              onChangeBranch={() => setBranchPicker(true)}
              branchName={bill.franchise_name || branchName(bill.franchise_id)}
            />
          ) : null}
          {current === STEP.bill ? <BillStep bill={bill} onEdit={() => go(STEP.services)} /> : null}
          {current === STEP.pay ? <PaymentStep bill={bill} pay={pay} onPay={setPay} /> : null}
          {current === STEP.review ? <ReviewStep bill={bill} managerName={managerName} onManagerName={setManagerName} /> : null}
          {current === STEP.approve ? <ApproveStep bill={bill} /> : null}
          {current === STEP.deliver ? <DeliverStep bill={bill} /> : null}
        </section>

        <CartPanel
          lines={cartLines}
          totals={onServices ? cart.totals : savedTotals(bill)}
          editable={onServices}
          stylists={stylistNames}
          offers={cart.offers}
          onOffers={cart.setOffers}
          onLineChange={cart.update}
          onLineRemove={cart.remove}
          cta={cta}
          extraActions={extraActions}
          hint={hint || (onServices && cart.dirty ? 'Unsaved changes — Save bill to keep them.' : '')}
          branchName={bill.franchise_name}
        />
      </div>

      {branchPicker ? (
        <BranchPickerModal
          title="Change the branch for this bill"
          subtitle="Prices of the services on the order move to the new branch's list."
          busy={busy}
          onPick={changeBranch}
          onClose={() => setBranchPicker(false)}
        />
      ) : null}
    </div>
  );
}
